import { Button, Empty, Flex, Form, Input, message, Skeleton, Tag } from "antd";
import styled from "styled-components";
import { QRCodeSVG } from "qrcode.react";
import PageLayout from "layouts/agencyPortal/dashboard/pageLayout";
import { useFetchAgencyQuery } from "features/basicInfo/agencyPortal/agencySettingsAPI";
import {
  useCreateMfaDeviceMutation,
  useDisableMfaDeviceMutation,
  useFetchAllMfaDevicesQuery,
  useVerifyMfaCodeMutation,
} from "features/auth/authAPI";
import { useEffect, useState, useCallback, Fragment } from "react";
import { useLocation, useNavigate } from "react-router-dom";

const StyledForm = styled(Form)`
  width: 350px;
  margin: 0 auto;
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
`;

const StyledTitle = styled.h2`
  text-align: center;
`;

const StyledText = styled.p`
  text-align: center;
`;

const StyledButton = styled(Button)`
  width: 100%;
  height: 50px;
`;

const OTPContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  gap: 8px;
  margin: 16px 0;
`;

const StyledInput = styled(Input)`
  width: 32px;
  height: 40px;
  text-align: center;
`;

const QRCodeContainer = styled.div`
  display: flex;
  justify-content: center;
  margin: 20px 0;
`;

const DeviceContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border: 1px solid #e8e8e8;
  border-radius: 8px;
  margin-bottom: 12px;
  background: white;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
`;

const DeviceInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const DeviceIcon = styled.div`
  color: #1890ff;
  font-size: 24px;
`;

const DeviceDetails = styled.div`
  display: flex;
  flex-direction: column;
`;

const DeviceTitle = styled.div`
  font-weight: 500;
  margin-bottom: 4px;
`;

const StyledIdText = styled.span`
  color: #666;
`;

const ClearLink = styled(Button)`
  padding: 0;
  height: auto;
  color: #1890ff;
  &:hover {
    color: #40a9ff;
    background: transparent;
  }
`;

function SetupMFAPage() {
  const { showTopBar } = useLocation().state || { showTopBar: true };
  const [createMfaDevice, { isLoading: isCreatingMfaDevice }] = useCreateMfaDeviceMutation();
  const [verifyMfaCode] = useVerifyMfaCodeMutation();
  const [deleteMfaDevice, { isLoading: isDeletingMfaDevice }] = useDisableMfaDeviceMutation();
  const { data: mfaDevices, isLoading: isMfaDevicesLoading } = useFetchAllMfaDevicesQuery();
  const { data: agencySettings, isLoading: isAgencySettingsLoading } = useFetchAgencyQuery();
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const [isDeviceCreated, setIsDeviceCreated] = useState(false);
  const [provisioningUri, setProvisioningUri] = useState("");
  const [deviceType, setDeviceType] = useState("");
  const [newDevicePersistentId, setNewDevicePersistentId] = useState("");

  const confirmedMfaDevices = mfaDevices?.filter((device) => device.confirmed);
  const hasTotp = confirmedMfaDevices?.some((device) => device.deviceType === "totp");
  const hasEmail = confirmedMfaDevices?.some((device) => device.deviceType === "email");
  const hasAllDeviceTypes = hasTotp && hasEmail;
  const hasNoDevices = !confirmedMfaDevices?.length;

  // If the agency has explicitly disabled MFA, don't show the management page
  // even when the user still has stale devices on their account.
  useEffect(() => {
    if (!isAgencySettingsLoading && agencySettings && !agencySettings.isMfaEnabled) {
      navigate("/accounts");
    }
  }, [isAgencySettingsLoading, agencySettings, navigate]);

  const handleSetupMFA = useCallback(
    async (newDeviceType) => {
      const result = await createMfaDevice({ deviceType: newDeviceType });
      if ("data" in result) {
        localStorage.removeItem("mfaSetupPending");
        localStorage.setItem(
          "mfaSetupPending",
          JSON.stringify({
            startTime: Date.now(),
            persistentId: result.data.persistentId,
            newDeviceType,
          }),
        );
        setIsDeviceCreated(true);
        setDeviceType(newDeviceType);
        setProvisioningUri(result.data?.provisioningUri);
        setNewDevicePersistentId(result.data.persistentId);
      }
      return result;
    },
    [createMfaDevice],
  );

  const verifyEmail = async () => {
    const result = await handleSetupMFA("email");
    if ("data" in result) {
      message.success("Please check your email inbox for a verification code.");
    }
  };

  const setupMFADeviceType = async (newDeviceType) => {
    setDeviceType(newDeviceType);
    if (newDeviceType === "email") {
      await verifyEmail();
    } else if (newDeviceType === "totp") {
      await handleSetupMFA(newDeviceType);
    }
  };

  useEffect(() => {
    // Check for pending MFA setup on component mount
    const pendingSetup = localStorage.getItem("mfaSetupPending");
    if (pendingSetup) {
      const { startTime, newDeviceType } = JSON.parse(pendingSetup);
      const timeElapsed = Date.now() - startTime;
      const tenMinutes = 10 * 60 * 1000;
      // The pending device is only stale if the user never confirmed that
      // specific type. With multi-device support, mfaDevices.length can be >0
      // even when the pending device is still unconfirmed.
      const pendingDeviceAlreadyConfirmed = mfaDevices?.some(
        (device) => device.confirmed && device.deviceType === newDeviceType,
      );

      // If more than 10 minutes have passed and the pending device is unconfirmed
      if (timeElapsed > tenMinutes && !pendingDeviceAlreadyConfirmed) {
        // Re-create so the user has a fresh code
        handleSetupMFA(newDeviceType);
      }
      // If less than 10 minutes, set new timeout for remaining time
      else if (timeElapsed < tenMinutes) {
        const remainingTime = tenMinutes - timeElapsed;
        setTimeout(async () => {
          if (!pendingDeviceAlreadyConfirmed) {
            handleSetupMFA(newDeviceType);
          }
        }, remainingTime);
      }
    }
  }, [mfaDevices, handleSetupMFA]);

  const onDelete = async (deviceId) => {
    const result = await deleteMfaDevice({
      persistentId: deviceId,
    });
    if ("data" in result) {
      message.success("MFA device deleted successfully");
      setIsDeviceCreated(false);
      setDeviceType("");
      setNewDevicePersistentId("");
      form.resetFields();
    }
  };

  const onFinish = async (values) => {
    const result = await verifyMfaCode({
      persistentId: newDevicePersistentId,
      token: values.code.join(""),
    });
    if ("data" in result) {
      message.success("MFA device added successfully");
      localStorage.removeItem("mfaSetupPending");
      setIsDeviceCreated(false);
      setDeviceType("");
      setNewDevicePersistentId("");
      form.resetFields();
      // For the post-login forced-setup flow (showTopBar=false), the user should land
      // on accounts immediately. From the standard Manage MFA entry point we stay on
      // the page so the user can add another device or remove one.
      if (!showTopBar) {
        navigate("/accounts");
      }
    }
  };

  const handleInput = (e) => {
    const input = e.target;
    const { value } = input;
    // Only allow single digit numbers
    if (/^\d$/.test(value)) {
      // Move to next input if available and not the last input
      if (input.nextElementSibling && input.nextElementSibling.nextElementSibling) {
        input.nextElementSibling.focus();
      }
    } else {
      input.value = "";
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const paste = e.clipboardData.getData("text").trim();
    // Only allow numbers in paste
    const hasExtraChars = paste.match(/[^0-9]/g);
    if (!hasExtraChars) {
      const digits = paste.split("").slice(0, 6);
      if (digits.length < 6) {
        digits.push(...Array(6 - digits.length).fill(""));
      }
      form.setFieldsValue({
        code: digits,
      });
      // Focus the last input
      const inputs = Array.from(e.target.closest("form").querySelectorAll("input"));
      inputs[paste.length]?.focus();
    }
  };

  const onKeyUp = (e, index) => {
    if (e.key === "Backspace" && !e.target.value && index > 0) {
      e.target.previousElementSibling?.focus();
    }
  };

  const handleClear = () => {
    form.setFieldsValue({
      code: Array(6).fill(""),
    });
    // Focus the first input after clearing
    const inputs = document.querySelectorAll("input");
    inputs[0]?.focus();
  };

  const renderOTPInputs = () => (
    <Form.List name="code" initialValue={Array(6).fill("")}>
      {(fields) => (
        <OTPContainer>
          {fields.map((field, index) => (
            <Form.Item {...field} key={field.key} noStyle rules={[{ required: true, message: "" }]}>
              <StyledInput
                maxLength={1}
                onKeyUp={(e) => onKeyUp(e, index)}
                onInput={handleInput}
                onPaste={handlePaste}
                inputMode="numeric"
                pattern="[0-9]*"
              />
            </Form.Item>
          ))}
          <ClearLink type="link" onClick={handleClear}>
            Clear
          </ClearLink>
        </OTPContainer>
      )}
    </Form.List>
  );

  const Container = showTopBar ? PageLayout : Fragment;

  if (isMfaDevicesLoading || isAgencySettingsLoading) {
    return (
      <Container>
        <Skeleton active />
      </Container>
    );
  }

  return (
    <Container>
      <StyledTitle>Manage MFA</StyledTitle>

      {confirmedMfaDevices?.length > 0 && (
        <div>
          {confirmedMfaDevices.map((device) => (
            <DeviceContainer key={device.persistentId}>
              <DeviceInfo>
                <DeviceIcon>{device.deviceType === "totp" ? "📱" : "📧"}</DeviceIcon>
                <DeviceDetails>
                  <DeviceTitle>
                    {device.deviceType === "totp" ? "Authenticator App" : "Email"}
                  </DeviceTitle>
                  <div>
                    <Tag color="green">ENABLED</Tag>
                    <StyledIdText>{device.persistentId}</StyledIdText>
                  </div>
                </DeviceDetails>
              </DeviceInfo>
              <Button
                type="primary"
                danger
                loading={isDeletingMfaDevice}
                onClick={async () => {
                  await onDelete(device.persistentId);
                }}
              >
                Delete
              </Button>
            </DeviceContainer>
          ))}
        </div>
      )}

      {!hasAllDeviceTypes && (
        <StyledForm form={form} onFinish={onFinish}>
          {isCreatingMfaDevice || isDeletingMfaDevice ? (
            <Skeleton active />
          ) : (
            <>
              {/* Show choices only when no setup is in progress */}
              {deviceType === "" && !isDeviceCreated && (
                <Empty
                  description={
                    <div>
                      <p>
                        {hasNoDevices
                          ? "You currently do not have any MFA devices set up. Select one of the following options:"
                          : "Add another verification method:"}
                      </p>
                      <Flex vertical>
                        {!hasTotp && (
                          <Button type="link" onClick={() => setupMFADeviceType("totp")}>
                            TOTP Authenticator App
                          </Button>
                        )}
                        {!hasEmail && (
                          <Button type="link" onClick={() => setupMFADeviceType("email")}>
                            Email
                          </Button>
                        )}
                      </Flex>
                    </div>
                  }
                />
              )}
              {/* TOTP setup flow */}
              {deviceType === "totp" && !hasTotp && (
                <>
                  <QRCodeContainer>
                    <QRCodeSVG value={provisioningUri} />
                  </QRCodeContainer>
                  <StyledText>
                    Scan the QR code with your authenticator app, and enter the 6-digit code
                  </StyledText>
                  {isDeviceCreated && (
                    <>
                      {renderOTPInputs()}
                      <Form.Item>
                        <StyledButton type="primary" htmlType="submit">
                          Setup
                        </StyledButton>
                      </Form.Item>
                    </>
                  )}
                </>
              )}
              {/* Email setup flow */}
              {deviceType === "email" && !hasEmail && isDeviceCreated && (
                <>
                  <div>
                    A verification code has been sent to your email. Please enter the 6-digit code
                    below.
                  </div>
                  {renderOTPInputs()}
                  <Form.Item>
                    <StyledButton type="primary" htmlType="submit">
                      Setup
                    </StyledButton>
                  </Form.Item>
                </>
              )}
              {deviceType !== "" && (
                <Button
                  type="link"
                  onClick={() => {
                    setDeviceType("");
                    setIsDeviceCreated(false);
                    setNewDevicePersistentId("");
                  }}
                >
                  Back
                </Button>
              )}
            </>
          )}
        </StyledForm>
      )}
    </Container>
  );
}

export default SetupMFAPage;
